import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AlertManagementPanel = ({ alerts, onAcknowledge, onResolve, onEscalate, onCreateAlert }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [newAlert, setNewAlert] = useState({
    alertType: 'service_outage',
    severity: 'high',
    priority: 'P1',
    title: '',
    description: '',
    affectedService: '',
    notificationChannels: ['email']
  });

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-red-600 bg-red-50 border-red-200',
      high: 'text-orange-600 bg-orange-50 border-orange-200',
      medium: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      low: 'text-blue-600 bg-blue-50 border-blue-200'
    };
    return colors?.[severity] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      P0: 'System Down',
      P1: 'Critical Degradation',
      P2: 'Performance Impact',
      P3: 'Minor Issues'
    };
    return labels?.[priority] || priority;
  };

  const filteredAlerts = alerts?.filter(alert => {
    if (filterSeverity !== 'all' && alert?.severity !== filterSeverity) return false;
    if (filterPriority !== 'all' && alert?.priority !== filterPriority) return false;
    return true;
  }) || [];

  const handleCreateAlert = () => {
    if (!newAlert?.title || !newAlert?.description) {
      return;
    }
    onCreateAlert(newAlert);
    setShowCreateModal(false);
    setNewAlert({
      alertType: 'service_outage',
      severity: 'high',
      priority: 'P1',
      title: '',
      description: '',
      affectedService: '',
      notificationChannels: ['email']
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Icon name="Bell" className="w-5 h-5 text-blue-600" />
            Alert Management Interface
          </h3>
          <p className="text-sm text-gray-600 mt-1">Multi-channel notification delivery with role-based routing</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Icon name="Plus" className="w-4 h-4 mr-2" />
          Create Alert
        </Button>
      </div>
      {/* Filters */}
      <div className="flex gap-4">
        <Select
          value={filterSeverity}
          onChange={(e) => setFilterSeverity(e?.target?.value)}
          className="w-48"
        >
          <option value="all">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </Select>
        <Select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e?.target?.value)}
          className="w-48"
        >
          <option value="all">All Priorities</option>
          <option value="P0">P0: System Down</option>
          <option value="P1">P1: Critical</option>
          <option value="P2">P2: Performance</option>
          <option value="P3">P3: Minor</option>
        </Select>
      </div>
      {/* Alerts List */}
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {filteredAlerts?.length > 0 ? (
          filteredAlerts?.map((alert, index) => (
            <div
              key={alert?.id || index}
              className={`border rounded-lg p-4 ${getSeverityColor(alert?.severity)}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${
                      getSeverityColor(alert?.severity)
                    }`}>
                      {alert?.priority}: {getPriorityLabel(alert?.priority)}
                    </span>
                    <span className="text-xs text-gray-600">
                      {new Date(alert?.created_at)?.toLocaleString()}
                    </span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-1">{alert?.title}</h4>
                  <p className="text-sm text-gray-700">{alert?.description}</p>
                  {alert?.affected_service && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                      <Icon name="Server" className="w-4 h-4" />
                      <span>Affected: {alert?.affected_service}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Alert Actions */}
              <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
                {alert?.alert_status === 'active' && (
                  <>
                    <Button
                      onClick={() => onAcknowledge(alert?.id)}
                      size="sm"
                      variant="outline"
                    >
                      <Icon name="Check" className="w-4 h-4 mr-1" />
                      Acknowledge
                    </Button>
                    <Button
                      onClick={() => onEscalate(alert?.id)}
                      size="sm"
                      variant="outline"
                      className="border-orange-500 text-orange-600 hover:bg-orange-50"
                    >
                      <Icon name="ArrowUp" className="w-4 h-4 mr-1" />
                      Escalate
                    </Button>
                  </>
                )}
                {alert?.alert_status === 'acknowledged' && (
                  <Button
                    onClick={() => onResolve(alert?.id)}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Icon name="CheckCircle" className="w-4 h-4 mr-1" />
                    Resolve
                  </Button>
                )}
                {alert?.notification_channels && (
                  <div className="ml-auto flex items-center gap-2">
                    {alert?.notification_channels?.map((channel, idx) => (
                      <span key={idx} className="text-xs text-gray-600 flex items-center gap-1">
                        <Icon
                          name={channel === 'email' ? 'Mail' : channel === 'sms' ? 'MessageSquare' : 'Bell'}
                          className="w-3 h-3"
                        />
                        {channel}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <Icon name="Bell" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Alerts</h3>
            <p className="text-gray-600">All systems operational</p>
          </div>
        )}
      </div>
      {/* Create Alert Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Create New Alert</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="X" className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Alert Title</label>
                <Input
                  value={newAlert?.title}
                  onChange={(e) => setNewAlert({ ...newAlert, title: e?.target?.value })}
                  placeholder="Brief description of the issue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={newAlert?.description}
                  onChange={(e) => setNewAlert({ ...newAlert, description: e?.target?.value })}
                  placeholder="Detailed information about the alert"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                  <Select
                    value={newAlert?.severity}
                    onChange={(e) => setNewAlert({ ...newAlert, severity: e?.target?.value })}
                  >
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <Select
                    value={newAlert?.priority}
                    onChange={(e) => setNewAlert({ ...newAlert, priority: e?.target?.value })}
                  >
                    <option value="P0">P0: System Down</option>
                    <option value="P1">P1: Critical</option>
                    <option value="P2">P2: Performance</option>
                    <option value="P3">P3: Minor</option>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Affected Service</label>
                <Input
                  value={newAlert?.affectedService}
                  onChange={(e) => setNewAlert({ ...newAlert, affectedService: e?.target?.value })}
                  placeholder="Service name"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <Button
                onClick={() => setShowCreateModal(false)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateAlert}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Create Alert
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertManagementPanel;
